import { useI18n } from 'next-localization';
import BaseSearch from 'components/Generic/SearchBar/BaseSearch';
import { useLazyFetchSearchResultsQuery } from '@/store/fundCentre/services/fundCentreAPI';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Props = {
  allowSearchByISIN: boolean;
  label: string;
  cookieNameProp: string;
};

const FundsSearch = ({ allowSearchByISIN, cookieNameProp, label }: Props) => {
  const { t } = useI18n();
  const [fetchSearch, { isFetching }] = useLazyFetchSearchResultsQuery();
  const [data, setData] = useState({
    searchMessageText: '',
    results: [],
  });
  const { sitecoreContext: context } = useSitecoreContext();

  const {
    fundCentreConfiguration: { countryInvestors },
    language,
    site: { name },
  } = context;

  const appliedFilters = useSelector(
    (state) => state.fundCentre.appliedFilters,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );

  const payload = useMemo(() => {
    return {
      countryInvestors,
      filters: appliedFilters,
      language,
      site: name,
    };
  }, [countryInvestors, appliedFilters, language, name]);

  const debouncedRequestCall = useMemo(() => {
    return debounce(async (searchInputData: string) => {
      try {
        const { data } = await fetchSearch({
          ...payload,
          searchQuery: {
            name: searchInputData,
          },
        });
        if (data) {
          const { products } = data;
          setData(() => ({
            searchMessageText: products.length > 0 ? '' : t('noResultsMessage'),
            results: products,
          }));
        }
      } catch (e) {
        console.error('Search fetching error: ', e);
      }
    }, 300);
  }, [payload, fetchSearch, t]);

  useEffect(() => {
    return () => {
      debouncedRequestCall.cancel();
    };
  }, [debouncedRequestCall]);

  return (
    <>
      <h3>{t('fundCentreSearchTitle')}</h3>
      <BaseSearch
        allowSearchByISIN={allowSearchByISIN}
        cookieName={`${cookieNameProp}#${context.site?.name || ''}`}
        label={label}
        placeholder={t('fundCentreSearchDescription')}
        fieldName="fundSearch"
        searchResultsProp={data}
        getSuggestions={debouncedRequestCall}
        isFetching={isFetching}
      />
    </>
  );
};

export default FundsSearch;
